import { useEffect, useState } from 'react'
import { usePageActions } from '../AppLayout'
import { api } from '../api/client'
import type { WatchlistItem, WatchlistQuery } from '../api/client'
import { useHasPermission } from '../api/session'
import { useListParams } from '../hooks/useListParams'
import { useZielwaehrung } from '../hooks/useZielwaehrung'
import { useListQuery } from '../hooks/useListQuery'
import { Card } from '../components/card/Card'
import { Button } from '../components/common/Button'
import { Chip } from '../components/common/Chip'
import { WatchlistDetail } from './WatchlistDetail'
import { AufnehmenDialog } from './WatchlistDialogs'
import { Input, Select } from '../components/common/Controls'
import { EmptyState, ErrorBox } from '../components/common/Feedback'
import { FilterBar, FilterEnd, FilterFixed, FilterGrow }
  from '../components/common/FilterBar'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { DataTable } from '../components/table/DataTable'
import type { Column } from '../components/table/types'
import { Refs, instrumentRef } from '../components/common/Refs'
import { geld } from './priceFormat'
import s from './WatchlistPage.module.scss'

const PERMISSION = 'watchlist:read'

//: Das Schreibrecht ist ein EIGENES: Wer die Liste sehen darf, darf sie
//: nicht schon deshalb ändern — auf ihr stehen die Reports.
const WRITE_PERMISSION = 'watchlist:write'

const PAGE_SIZE = 50

const datum = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' })

const COLUMNS: Column<WatchlistItem>[] = [
  { key: 'name', label: 'Name', sortable: true, minWidth: '240px',
    // DIESELBE ZELLE WIE BEI DEN MELDUNGEN: Ein Klick auf den Namen klappt
    // die Eckdaten auf — ISIN mit Kopierknopf, Kurs, Ampel und die jüngsten
    // Meldungen — statt sofort wegzunavigieren.
    //
    // Der Klick erreicht die ZEILE nicht: Die klappt den Detailbereich auf
    // und zu, und wer den Namen antippt, will das Popover und nicht das
    // Zuklappen dessen, was er gerade liest.
    render: (row) => <Refs leer="—"
      refs={[instrumentRef(row.isin, row.name, row.instrument_id)]} />,
    help: 'Ein Klick auf den Namen zeigt die Eckdaten des Titels — Kennung, Kurs, Ampel und die jüngsten Meldungen. Auf die Stammdaten führt der Knopf darin.' },
  // Nach dem Namen, weil beides zusammen die Frage "was ist das?"
  // beantwortet — die ISIN sagt nur, welches Papier gemeint ist.
  { key: 'sector_name', label: 'Sektor', minWidth: '170px', hideOn: 'mobile',
    // NICHT SORTIERBAR: Der Server kennt `sector_name` in dieser Liste nicht
    // als Sortierschlüssel. Ein klickbarer Kopf, der nichts tut, wäre
    // schlimmer als keiner.
    render: (row) => row.sector_name
      ?? <span className={s.none} title="keine Einordnung hinterlegt">—</span>,
    help: 'Der GICS-Sektor. Fehlt bei ETFs und den wenigen Titeln ohne Einordnung: Ein Fonds KANN keinen eigenen Geschäftszweck haben — er hält Titel, die einen haben.' },
  { key: 'isin', label: 'ISIN', sortable: true, mono: true, copy: true,
    minWidth: '190px', hideOn: 'mobile',
    help: 'Internationale Wertpapierkennnummer des beobachteten Titels.' },
  { key: 'quote', label: 'Kurs', align: 'right', mono: true, minWidth: '130px',
    // Wie in der Ideenliste nicht sortierbar: Der Kurs kommt aus einer
    // zweiten Tabelle und wird erst nach dem Blättern angehängt.
    render: (row) => row.quote
      ? <span title={row.quote_at ? `Stand ${datum.format(new Date(row.quote_at))}` : undefined}>
          {geld(row.quote)}
        </span>
      : <span className={s.none} title="kein aktueller Kurs">—</span>,
    help: 'Der jüngste Kurs am Hauptplatz — aus Tageskursen und Schlusskursen der jüngere. Ein Strich heißt NICHT „kein Kurs vorhanden", sondern „kein aktueller": Ein veralteter wird unterdrückt, weil er sonst eine Aktualität behauptete, die es nicht gibt. Der Stand steht im Tooltip.' },
  { key: 'reasons', label: 'Gründe', minWidth: '260px',
    render: (row) => <Reasons values={row.reasons} />,
    help: 'Warum der Titel beobachtet wird. Mehrere möglich — ein Titel steht nur EINMAL auf der Liste, die Gründe sammeln sich am selben Eintrag.' },
  { key: 'active', label: 'Status', sortable: true, minWidth: '120px',
    render: (row) => row.active
      ? <Chip tone="info">aktiv</Chip>
      : <Chip>ruhend</Chip>,
    help: 'Aktiv heißt: steht unter Beobachtung. Ruhend heißt NICHT abgeschlossen, sondern: bleibt interessant, wird aber gerade nicht verfolgt.' },
  { key: 'created', label: 'Seit', sortable: true, mono: true, align: 'right',
    minWidth: '120px', hideOn: 'tablet',
    render: (row) => datum.format(new Date(row.created)) },
]

export function WatchlistPage() {
  const allowed = useHasPermission(PERMISSION)
  const darfSchreiben = useHasPermission(WRITE_PERMISSION)
  const [aufnehmen, setAufnehmen] = useState(false)

  const list = useListParams({
    // `currency` steht NICHT dabei: Ein Filter entscheidet, WELCHE Titel
    // kommen — die Zielwährung nur, wie ihr Kurs aussieht.
    filters: { q: '', status: 'active', reason: '' },
    sort: 'name',
  })

  const { waehrung, wahl } = useZielwaehrung(allowed)

  // OBEN IN DER KOPFZEILE, wie bei den anderen Ansichten — nicht in der
  // Filterleiste: Ein Filter schränkt die Auswahl ein, die Zielwährung
  // ändert nur die Darstellung.
  usePageActions(wahl)

  const params: WatchlistQuery = {
    q: list.filters.q.trim() || undefined,
    // Als Liste, obwohl die Auswahl nur eine zulässt: Die Schnittstelle
    // nimmt mehrere und rechnet in jede um.
    currency: waehrung ? [waehrung] : undefined,
    status: list.filters.status as WatchlistQuery['status'],
    reason: list.filters.reason || undefined,
    sort: list.sort as WatchlistQuery['sort'],
    dir: list.descending ? 'desc' : 'asc',
    page: list.page,
    size: PAGE_SIZE,
  }

  const { result, loading, error, denied, reload } = useListQuery(
    api.watchlist, params, allowed)

  // Die Gründe für die Auswahlliste, einmal geladen. Sie ändern sich nur, wenn
  // jemand einen Eintrag anlegt — nicht beim Blättern oder Filtern.
  const [reasons, setReasons] = useState<string[]>([])
  const [gewaehlt, setGewaehlt] = useState<number | null>(null)
  useEffect(() => {
    if (!allowed) return
    let current = true
    api.watchlistReasons()
      .then((answer) => { if (current) setReasons(answer) })
      .catch(() => { /* Bleibt die Liste leer, filtert man eben nicht. */ })
    return () => { current = false }
  }, [allowed])

  if (!allowed || denied) {
    return <PermissionNotice permission={PERMISSION} subject="die Watchlist" />
  }

  const zeilen = result?.items ?? []
  // Nur aufklappen, was in der Liste auch steht: Nach einem
  // Filterwechsel kann die gewählte Zeile verschwunden sein, und ein
  // Detailbereich ohne zugehörige Zeile hätte keinen Bezug mehr.
  const offen = zeilen.find((z) => z.id === gewaehlt) ?? null

  return (
    <>
      <FilterBar>
        <FilterGrow>
          <Input label="Suche"
                 placeholder="Name oder Symbol — mindestens zwei Zeichen"
                 value={list.filters.q} autoFocus
                 onChange={(event) => list.setFilter('q', event.target.value)} />
        </FilterGrow>
        <FilterFixed>
          <Select label="Status" value={list.filters.status}
                  onChange={(event) =>
                    list.setFilter('status', event.target.value)}>
            <option value="active">nur aktive</option>
            <option value="resting">nur ruhende</option>
            <option value="all">alle</option>
          </Select>
        </FilterFixed>
        <FilterFixed>
          <Select label="Grund" value={list.filters.reason}
                  disabled={reasons.length === 0}
                  onChange={(event) =>
                    list.setFilter('reason', event.target.value)}>
            <option value="">alle Gründe</option>
            {reasons.map((reason) => (
              <option key={reason} value={reason}>{reason}</option>
            ))}
          </Select>
        </FilterFixed>
        <FilterEnd>
          <Button onClick={list.reset} disabled={!list.touched}>
            Zurücksetzen
          </Button>
          {/*
            Der einzige schreibende Knopf der Oberfläche. Ohne das Recht
            steht er gar nicht da: Ein Knopf, der nur ins 403 führt, ist
            schlimmer als keiner.
          */}
          {darfSchreiben && (
            <Button variant="primary" onClick={() => setAufnehmen(true)}>
              + Titel aufnehmen
            </Button>
          )}
        </FilterEnd>
      </FilterBar>

      {error && <ErrorBox>{error}</ErrorBox>}

      <Card flush span={12}>
        <DataTable
          columns={COLUMNS}
          rows={zeilen}
          rowKey={(row) => row.id}
          sort={{ key: list.sort, descending: list.descending }}
          onSort={(next) => list.setSort(next.key, next.descending)}
          page={result ? { page: result.page, size: result.size,
                           total: result.total, hasMore: result.has_more }
                       : undefined}
          onPage={list.setPage}
          // Die Zeile klappt auf, statt auf das Instrument zu springen.
          // Dorthin führt jetzt der Titel im Kopf des Detailbereichs —
          // dieselbe Möglichkeit, nur nicht mehr als Nebenwirkung eines
          // Klicks, der etwas anderes zu tun scheint.
          onRowClick={(row) =>
            setGewaehlt(gewaehlt === row.id ? null : row.id)}
          rowAttrs={(row): Record<string, string> =>
            row.id === offen?.id ? { 'data-active': 'true' } : {}}
          loading={loading && result === null}
          empty={<EmptyState title="keine Einträge">
            {list.touched
              ? 'Kein Titel passt zu diesen Filtern.'
              : 'Es wird derzeit kein Titel beobachtet.'}
          </EmptyState>}
        />
      </Card>

      {/*
        Der Detailbereich UNTER der Liste, nicht daneben: Die Tabelle nutzt
        die volle Breite, und ein Seitenbereich zwänge sie in die Hälfte —
        für die paar Zeilen, die hier stehen, ein schlechter Tausch.
      */}
      {offen && <WatchlistDetail eintrag={offen} onGeaendert={reload} />}

      {aufnehmen && (
        <AufnehmenDialog
          onFertig={() => { setAufnehmen(false); reload() }}
          onAbbrechen={() => setAufnehmen(false)} />
      )}
    </>
  )
}

/**
 * Die Gründe als Pillen nebeneinander.
 *
 * Zurückgenommen in Grau: Sie sind eine Einordnung, keine Bewertung — anders
 * als der Status, der die eine Frage beantwortet, um die es in der Liste geht.
 */
function Reasons({ values }: { values: string[] }) {
  if (values.length === 0) return <span className={s.none}>—</span>
  return (
    <span className={s.reasons}>
      {values.map((value) => <Chip key={value}>{value}</Chip>)}
    </span>
  )
}
